/*
 * VNCcommander - The brilliant centerpiece of VNClagoon with your activity stream and much more.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import {
  ChangeDetectorRef,
  ComponentFactoryResolver,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef
} from "@angular/core";

import { MentionConfig } from "./mention-config";
import { MentionListComponent } from "./mention-list.component";
import { getCaretPosition, getValue, insertValue, setCaretPosition } from "./mention-utils";
import { environment } from "src/environments/environment";

const KEY_BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESCAPE = 27;
const KEY_SPACE = 32;
const KEY_LEFT = 37;
const KEY_UP = 38;
const KEY_RIGHT = 39;
const KEY_DOWN = 40;
const KEY_2 = 50;

@Directive({
  selector: "[vpMention]"
})
export class MentionDirective implements OnChanges {
  // stores the items passed to the mentions directive and used to populate the root items in mentionConfig
  private mentionItems: any[];
  searchUsers$: any;

  @Input("vpMention") set mention(items: any[]) {
    this.mentionItems = items;
  }

  // the provided configuration object
  @Input() mentionConfig: MentionConfig = { items: [] };

  private activeConfig: MentionConfig; // = this.DEFAULT_CONFIG;

  private DEFAULT_CONFIG: MentionConfig = {
    items: [],
    triggerChar: "@",
    labelKey: "label",
    maxItems: -1,
    mentionSelect: (item: any) => this.activeConfig.triggerChar + item[this.activeConfig.labelKey]
  };

  // template to use for rendering list items
  @Input() mentionListTemplate: TemplateRef<any>;

  // event emitted whenever the search term changes
  @Output() searchTerm = new EventEmitter();

  // option to diable internal filtering. can be used to show the full list returned
  // from an async operation (or allows a custom filter function to be used - in future)
  private disableSearch: boolean = false;

  private triggerChars: { [key: string]: MentionConfig } = {};

  searchString: string;
  startPos: number;
  startNode;
  searchList: MentionListComponent;
  stopSearch: boolean;
  iframe: any; // optional

  constructor(
    private _element: ElementRef,
    private _componentResolver: ComponentFactoryResolver,
    private _viewContainerRef: ViewContainerRef,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    
  }

  ngOnChanges(changes: SimpleChanges) {
    
    if (changes["mention"] || changes["mentionConfig"]) {
      this.updateConfig();
    }
    if (this.searchList) {
      this.searchList.hidden = true;
    }
  }

  private updateConfig() {
    let config = this.mentionConfig;
    this.triggerChars = {};
    // use items from directive if they have been set
    if (this.mentionItems) {
      config.items = this.mentionItems;
    }
    this.addConfig(config);
    // nested configs
    if (config.mentions) {
      config.mentions.forEach(config => this.addConfig(config));
    }
  }

  // add configuration for a trigger char
  private addConfig(config: MentionConfig) {
    // defaults
    let defaults = Object.assign({}, this.DEFAULT_CONFIG);
    config = Object.assign(defaults, config);
    // add the config
    this.triggerChars[config.triggerChar] = config;

    // for async update while menu/search is active
    if (this.activeConfig && this.activeConfig.triggerChar === config.triggerChar) {
      this.activeConfig = config;
      this.updateSearchList();
    }
  }

  setIframe(iframe: HTMLIFrameElement) {
    this.iframe = iframe;
  }

  stopEvent(event: any) {
    if (!event.wasClick) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }

  @HostListener("blur", ["$event"])
  blurHandler(event: any) {
    this.stopEvent(event);
    this.stopSearch = true;
    this.hideMentionList();
   
  }

  @HostListener("keydown", ["$event"])
  keyHandler(event: any, nativeElement: HTMLInputElement = this._element.nativeElement) {
    const config = this.triggerChars["@"];
    
    if (!config || (config && config.chatType !== "groupchat")) {
      this.hideMentionList();
      return;
    }
    let charPressed = event.key;
    if (environment.isCordova) {
      setTimeout(() => {
        let charPressed = nativeElement.value.slice(nativeElement.selectionStart - 1, nativeElement.selectionStart);
        
        return this.handleKeyDownEvent(event, nativeElement, charPressed);
      }, 300);
    } else {
      return this.handleKeyDownEvent(event, nativeElement, charPressed);
    }
  }

  private hideMentionList(): void {
    if (this.searchList) {
      this.searchList.hidden = true;
      if (document.querySelector("vp-mention-list") !== null) {
        document.querySelector("vp-mention-list").classList.add("hide-list");
      }
      this.searchList.className = "hide-list";
      this.changeDetectorRef.markForCheck();
    }
  }

  private handleKeyDownEvent(event, nativeElement, charPressed): boolean {

    let val: string = getValue(nativeElement);
    let pos = getCaretPosition(nativeElement, this.iframe);
    if (!environment.isCordova) {
      if (!charPressed) {
        let charCode = event.which || event.keyCode;
        if (!event.shiftKey && (charCode >= 65 && charCode <= 90)) {
          charPressed = String.fromCharCode(charCode + 32);
        } else {
          charPressed = String.fromCharCode(event.which || event.keyCode);
        }
      }
    }

    if (event.keyCode === KEY_ENTER && event.wasClick && pos < this.startPos) {
      // put caret back in position prior to contenteditable menu click
      pos = nativeElement.length;
      setCaretPosition(nativeElement, pos, this.iframe);
    }
    let config = this.triggerChars[charPressed];
   
    if (config) {
      this.activeConfig = config;
      this.startPos = pos;
      this.startNode = (this.iframe ? this.iframe.contentWindow.getSelection() : window.getSelection()).anchorNode;
      this.stopSearch = false;
      this.searchString = null;
      this.showSearchList(nativeElement);
      this.updateSearchList(nativeElement);
    } else if (this.startPos >= 0 && !this.stopSearch) {
      
      if (pos <= this.startPos && event.keyCode !== KEY_ENTER) {
        this.hideMentionList();
      } else if (event.keyCode !== KEY_SHIFT &&
        !event.metaKey &&
        !event.altKey &&
        !event.ctrlKey &&
        pos >= this.startPos
      ) {
        if (event.keyCode === KEY_BACKSPACE && pos > 0) {
          pos--;
          if (pos === this.startPos) {
            this.stopSearch = true;
          }
          this.searchList.hidden = this.stopSearch;
        } else if (this.searchList && !this.searchList.hidden) {
          if (event.keyCode === KEY_TAB || event.keyCode === KEY_ENTER) {
            
            this.stopEvent(event);
            this.hideMentionList();
            // value is inserted without a trailing space for consistency
            // between element types (div and iframe do not preserve the space)
            insertValue(nativeElement, this.startPos, pos,
              this.activeConfig.mentionSelect(this.searchList.activeItem), this.iframe);
            // fire input event so angular bindings are updated
            if ("createEvent" in document) {
              let evt = document.createEvent("HTMLEvents");
              evt.initEvent("input", false, true);
              nativeElement.dispatchEvent(evt);
            }
            this.startPos = -1;
            return false;
          } else if (event.keyCode === KEY_ESCAPE) {
            this.stopEvent(event);
            this.searchList.hidden = true;
            this.stopSearch = true;
            return false;
          } else if (event.keyCode === KEY_DOWN) {
            this.stopEvent(event);
            this.searchList.activateNextItem();
            return false;
          } else if (event.keyCode === KEY_UP) {
            this.stopEvent(event);
            this.searchList.activatePreviousItem();
            return false;
          }
        }

        if (event.keyCode === KEY_LEFT || event.keyCode === KEY_RIGHT) {
          // this.stopEvent(event);
          // return false;
          this.searchTerm.emit(this.searchString);
          this.updateSearchList(nativeElement);
        } else {
          let mention = "";
          if (environment.isCordova) {
            mention = val.substring(this.startPos, pos);
           
            //  const prevChar = val.substring(this.startPos - 2, 1);
           
            //  if (prevChar !== "@") {
            //   this.hideMentionList();
            //   return false;
            //  }
          } else {
           
            mention = val.substring(this.startPos + 1, pos);
            if (event.keyCode !== KEY_BACKSPACE) {
              mention += charPressed;
            }
          }

         
          this.searchString = mention;
          this.searchTerm.emit(this.searchString);
          this.updateSearchList(nativeElement);
        }
      }
    }
  }

  updateSearchList(nativeElement?: any) {
    let matches: any[] = [];
    if (!!nativeElement && this.searchList) {
      this.searchList.position(nativeElement, this.iframe, this.activeConfig.dropUp);
    }
    if (this.activeConfig && !!this.activeConfig.items && this.activeConfig.items.length > 0) {
      let objects = this.activeConfig.items;
      // disabling the search relies on the async operation to do the filtering
      if (!this.disableSearch && this.searchString) {
        let searchStringLowerCase = this.searchString.toLowerCase();
        objects = objects.filter(e => {
          return e.label && e.label.toLowerCase().indexOf(searchStringLowerCase) !== -1
            || e.value.toLowerCase().indexOf(searchStringLowerCase) !== -1;
        });
      } else {
        this.hideMentionList();
        this.searchList.items = [];
      }
      matches = objects;
      if (this.activeConfig.maxItems > 0) {
        matches = matches.slice(0, this.activeConfig.maxItems);
      }

      if (this.searchList) {
        if (this.searchString && this.searchString.length < 2 || !this.searchString) {
          this.hideMentionList();
          this.searchList.items = [];
        } else {
          this.searchList.labelKey = this.activeConfig.labelKey;
          this.searchList.items = matches;
          this.searchList.hidden = matches.length === 0;
          if (this.searchList.hidden) {
            this.hideMentionList();
          }
        }
      }
      
    } else {
      this.hideMentionList();
    }
    this.changeDetectorRef.markForCheck();
  }

  showSearchList(nativeElement: HTMLInputElement) {
    
    if (!this.searchList) {
      let componentFactory = this._componentResolver.resolveComponentFactory(MentionListComponent);
      let componentRef = this._viewContainerRef.createComponent(componentFactory);
      this.searchList = componentRef.instance;
      this.searchList.position(nativeElement, this.iframe, this.activeConfig.dropUp);
      this.searchList.itemTemplate = this.mentionListTemplate;
      this.searchList.className = "";
      componentRef.instance["itemClick"].subscribe(() => {
        
        if (!environment.isCordova) {
          let fakeKeydown = { "keyCode": KEY_ENTER, "wasClick": true };
          this.keyHandler(fakeKeydown, nativeElement);
        } else {
          let pos = getCaretPosition(nativeElement, this.iframe);
          
          setCaretPosition(nativeElement, pos, this.iframe);
          this.searchList.hidden = true;
          // value is inserted without a trailing space for consistency
          // between element types (div and iframe do not preserve the space)
          insertValue(nativeElement, this.startPos - 1, pos,
            this.activeConfig.mentionSelect(this.searchList.activeItem), this.iframe);
          // fire input event so angular bindings are updated
          if ("createEvent" in document) {
            let evt = document.createEvent("HTMLEvents");
            evt.initEvent("input", false, true);
            nativeElement.dispatchEvent(evt);
          }
          this.startPos = -1;
          this.changeDetectorRef.markForCheck();
        }
        setTimeout(() => {
          nativeElement.focus();
        }, 500);

      });
    } else if (!!this.searchList) {
      this.searchList.labelKey = this.activeConfig.labelKey;
      this.searchList.activeIndex = 0;
      this.searchList.className = "";
      if (document.querySelector("vp-mention-list") !== null) {
        document.querySelector("vp-mention-list").classList.remove("hide-list");
      }
      this.searchList.position(nativeElement, this.iframe, this.activeConfig.dropUp);
      window.setTimeout(() => this.searchList.resetScroll());
    }
  }
}
